import { useEffect, useMemo, useState } from 'react';
import { darkmodeMediaMatch, mediaQueryDesktop, Orientation, mediaQueryOrientationLandscape, UiContext, Provider, isInIframe, isTeams } from './uiCtx';

export const UIContext = ({ children }: { children?: React.ReactNode }) => {
    const [isDarkmode, setIsDarkmode] = useState(() => darkmodeMediaMatch.matches);
    const [isInDesktopMode, setIsInDesktopMode] = useState(() => mediaQueryDesktop.matches);
    const layout = useMemo(() => (isInDesktopMode ? 'desktop' : 'phone'), [isInDesktopMode]);
    const [orientation, setOrientation] = useState<Orientation>(() => (mediaQueryOrientationLandscape.matches ? 'landscape' : 'portrait'));
    const [showNavbar, setShowNavbar] = useState(!(isInIframe || isTeams));

    useEffect(() => {
        const handleOrientationChange = (e: MediaQueryListEvent) => {
            setOrientation(e.matches ? 'landscape' : 'portrait');
        };
        mediaQueryOrientationLandscape.addEventListener('change', handleOrientationChange);
        return () => {
            mediaQueryOrientationLandscape.removeEventListener('change', handleOrientationChange);
        };
    }, []);

    useEffect(() => {
        const handleLayoutChange = (e: MediaQueryListEvent) => {
            setIsInDesktopMode(e.matches);
        };
        mediaQueryDesktop.addEventListener('change', handleLayoutChange);
        return () => {
            mediaQueryDesktop.removeEventListener('change', handleLayoutChange);
        };
    }, []);

    useEffect(() => {
        const handleDarkmodeChange = (e: MediaQueryListEvent) => {
            setIsDarkmode(e.matches);
        };
        darkmodeMediaMatch.addEventListener('change', handleDarkmodeChange);
        return () => {
            darkmodeMediaMatch.removeEventListener('change', handleDarkmodeChange);
        };
    }, []);
    useEffect(() => {
        // needed to write css that only apply to darkmode & worked for the Layer/Callout's too.
        if (isDarkmode === undefined) {
            return;
        }

        const bodyClasslist = document.body.classList;
        if (!bodyClasslist) {
            return;
        }

        if (isDarkmode) {
            if (!bodyClasslist.contains('isDarkmode')) {
                bodyClasslist.add('isDarkmode');
            }
        } else if (bodyClasslist.contains('isDarkmode')) {
            bodyClasslist.remove('isDarkmode');
        }
    }, [isDarkmode]);

    const ctx = useMemo<UiContext>(
        () => ({
            isDarkmode,
            isTeams,
            isInIframe,
            isInDesktopMode,
            isInPhoneMode: !isInDesktopMode,
            layout,
            orientation,
            showNavbar,
            setShowNavbar,
        }),
        [isDarkmode, isInDesktopMode, layout, orientation, showNavbar],
    );

    return <Provider value={ctx}>{children}</Provider>;
};
